import PhoneField from '../PhoneField'
import EditorActions from 'components/Common/EditorActions'
import { InputOrPicker } from 'components/Common/DateTimeField'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { Link, Prompt } from 'react-router-dom'
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Breadcrumbs from 'components/Common/Breadcrumb'
import { get, post } from 'helpers/api_helper'
import { initialClubValues, validateClubCreation, buildClubCreationPayload, clubDays, clubAmenities, clubHourField } from './clubCreationForm'
import { PlayerCityPicker } from '../PlayerAccounts/PlayerCreationChoices'
import { ClubAddressPicker } from './ClubCreationChoices'
import { store as socketStore } from 'components/Common/Socket/store'
import { runClubCommand, clubInvitationLabels } from './clubCreationOperation'
import { readPending, savePending, newPlayerOperationId as newOperationId } from '../PlayerAccounts/playerInvitationState'
import ClubCreationSource from './ClubCreationSource'
import ClubTextBlocks from './ClubTextBlocks'
import './clubCreation.scss'

const usableSettings = options => ['activeSports', 'eligibleCountries'].every(key => Array.isArray(options?.[key]) && options[key].length) && Array.isArray(options?.courtSurfaces)

export function CreateClub(props) {
  return <ClubCreationContent key={(props.userDetails?.id || props.userDetails?._id || '') + ':' + (props.leadId || '')} {...props} />
}

function ClubCreationContent({ settings, history, location = {}, userDetails = {}, leadId }) {
  const { socket, socketClientId } = useContext(socketStore)?.state?.socket || {}
  const storageKey = 'sportya-club-creation:' + (userDetails.id || userDetails._id)
  const pending = useRef(readPending(storageKey))
  const request = useRef(null)
  const busy = useRef(false)
  const [status, setStatus] = useState(pending.current ? 'unknown' : 'idle')
  const [result, setResult] = useState(null)
  const [message, setMessage] = useState('')
  const locked = status === 'pending' || status === 'unknown'
  const track = useCallback(async (attempt, mode = 'create') => {
    if (busy.current || !attempt) return
    attempt = { ...attempt, correlationId: newOperationId() }
    busy.current = true; pending.current = attempt; savePending(storageKey, attempt)
    setStatus('pending'); setMessage('')
    const operation = runClubCommand({ ...attempt, socket, read: id => get('/admin/club-creations/' + id),
      submit: mode === 'create' && attempt.payload ? () => post('/admin/club-creations', {
        ...attempt.payload, operationId: attempt.operationId, socketClientId,
      }, { headers: { 'X-Correlation-Id': attempt.correlationId } }) : mode === 'retry' ? () => post(
        '/admin/club-creations/' + attempt.operationId + '/retry', { socketClientId }, { headers: { 'X-Correlation-Id': attempt.correlationId } }) : undefined,
    })
    request.current = operation
    const outcome = await operation.promise
    if (request.current !== operation) return
    busy.current = false; request.current = null
    if (outcome.status === 'cancelled') return
    if (outcome.status === 'saved') {
      setResult({ ...outcome, operationId: attempt.operationId }); pending.current = null; savePending(storageKey, null)
    } else if (outcome.code === 'CREATION_NOT_RECEIVED') {
      setMessage('The request has not reached the creation service. Re-enter the original details to resume the same operation.'); setStatus('resuming'); return
    } else if (outcome.status === 'error') {
      setMessage(outcome.message); pending.current = null; savePending(storageKey, null)
    }
    setStatus(outcome.status)
  }, [socket, socketClientId, storageKey])
  useEffect(() => {
    const timer = setTimeout(() => { if (pending.current) track(pending.current, 'check') }, 0)
    return () => { clearTimeout(timer); const operation = request.current; request.current = null; busy.current = false; operation?.cancel() }
  }, [track])
  useEffect(() => {
    if (!result?.operationId || !['pending', 'sending'].includes(result.invitationStatus)) return undefined
    let active = true, timer
    const started = Date.now()
    const refresh = async () => {
      try {
        const response = await get('/admin/club-creations/' + result.operationId)
        if (active && response?.data?.status === 'saved' && response.data.id === result.id) {
          setResult(current => ({ ...current, ...response.data }))
          if (!['pending', 'sending'].includes(response.data.invitationStatus)) return
        }
      } catch { /* Club persistence is independent from delivery availability. */ }
      if (!active) return
      if (Date.now() - started >= 60000) setResult(current => ({ ...current, invitationStatus: 'unconfirmed' }))
      else timer = setTimeout(refresh, 2000)
    }
    timer = setTimeout(refresh, 2000)
    return () => { active = false; clearTimeout(timer) }
  }, [result?.operationId, result?.id, result?.invitationStatus])
  const [options, setOptions] = useState(settings || {})
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState('')
  const [retry, setRetry] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [sourceBusy, setSourceBusy] = useState(Boolean(leadId))
  const [addressBusy, setAddressBusy] = useState(false)
  const [organizerBusy, setOrganizerBusy] = useState({})
  const choiceBusy = sourceBusy || addressBusy || Object.values(organizerBusy).some(Boolean)
  const [exit, setExit] = useState(null)
  const leaving = useRef(false)
  useEffect(() => {
    let current = true
    setLoading(true); setFailure('')
    get('/settings').then(response => {
      if (!usableSettings(response?.data)) throw new Error('Unavailable options')
      if (current) setOptions(response.data)
    }).catch(() => { if (current) setFailure('Club options could not be loaded. Retry before creating the club.') })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
  }, [retry])
  const form = useFormik({ initialValues: initialClubValues(), validate: values => validateClubCreation(values, options), onSubmit: values => {
    if (locked || busy.current || loading || failure || choiceBusy || !usableSettings(options)) return
    return track({ ...(pending.current || { operationId: newOperationId() }), payload: buildClubCreationPayload(values) })
  } })
  const unsaved = status !== 'saved' && (form.dirty || locked)
  useEffect(() => {
    if (!unsaved) return undefined
    const beforeUnload = event => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [unsaved])
  const focusError = name => {
    const element = document.getElementById('club-' + name) || document.getElementById('player-' + name)
    const details = element?.closest('details')
    if (details) details.open = true
    element?.focus()
  }
  const submit = async event => {
    event.preventDefault(); if (locked || busy.current) return
    setSubmitted(true)
    const errors = await form.validateForm()
    if (Object.keys(errors).length) { focusError(Object.keys(errors)[0]); return }
    await form.submitForm()
  }
  const field = (name, label, { required = false, type = 'text', choices, emptyLabel = 'Select…', onChange, ...props } = {}) => {
    const error = (submitted || form.touched[name]) && form.errors[name]
    if (type === 'tel') return <PhoneField key={name} id={'club-' + name} name={name} label={label} value={form.values[name]}
      disabled={locked} error={error} onBlur={form.handleBlur}
      onChange={value => { form.setFieldTouched(name, true, false); form.setFieldValue(name, value) }} />
    const shared = { id: 'club-' + name, name, value: form.values[name], onChange: event => { form.setFieldTouched(name, true, false); (onChange || form.handleChange)(event) },
      onBlur: form.handleBlur, className: 'form-control', 'aria-required': required,
      'aria-invalid': Boolean(error), 'aria-describedby': 'club-' + name + '-help', ...props }
    return <div className="form-group mb-0" key={name}>
      <label htmlFor={shared.id}>{label}{required && <span aria-hidden="true"> *</span>}</label>
      {choices ? <select {...shared}><option value="">{emptyLabel}</option>{choices.map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}</select>
        : type === 'textarea' ? <textarea {...shared} rows={4} /> : <InputOrPicker {...shared} type={type} />}
      <small id={shared.id + '-help'} className="text-danger">{error}</small>
    </div>
  }
  const section = (title, children) => <section className="card"><div className="card-body"><h5 className="mb-3">{title}</h5>{children}</div></section>
  const grid = children => <div className="club-creation-grid">{children}</div>
  return <div className="page-content"><Container fluid>
    <Breadcrumbs title="Clubs" path="/accounts/clubs" breadcrumbItem="Create club" />
    <div className="club-creation">
      <Prompt when={unsaved} message={(target, action) => {
        if (leaving.current) return true
        setExit({ target, action }); return false
      }} />
      <Modal isOpen={Boolean(exit)} toggle={() => setExit(null)}>
        <ModalHeader toggle={() => setExit(null)}>Leave club creation?</ModalHeader>
        <ModalBody>Your changes have not been saved.</ModalBody>
        <ModalFooter><button type="button" className="btn btn-light" onClick={() => setExit(null)}>Keep editing</button>
          <button type="button" className="btn btn-primary" onClick={() => {
            leaving.current = true; const target = exit; setExit(null)
            if (target.action === 'REPLACE') history.replace(target.target); else history.push(target.target)
          }}>Leave page</button></ModalFooter>
      </Modal>
      {status === 'saved' ? <section className="card"><div className="card-body" role="status"><h4>Club created</h4>
        <p>The club is Activated. Its owner stays Pending until they set a password in SportyaOS.</p>
        <p>{clubInvitationLabels[result?.invitationStatus] || clubInvitationLabels.unconfirmed}</p>
        {result?.indexStatus === 'failed' && <p className="text-warning">Search indexing is not confirmed. The club is saved and can be opened directly.</p>}
        <Link className="btn btn-primary" to={'/accounts/clubs/' + result.id}>Open club profile</Link>
      </div></section> : <form onSubmit={submit} noValidate aria-label="Create club">
      <p className="text-muted">Create the club and invite its owner to activate their account. Fields marked * are required.</p>
      <ClubCreationSource leadId={leadId} options={options} form={form} locked={locked} onBusy={setSourceBusy} />
      {loading && <p role="status">Loading club options…</p>}
      {failure && <div className="alert alert-warning" role="alert">{failure} <button type="button" className="btn btn-link p-0" onClick={() => setRetry(value => value + 1)}>Retry options</button></div>}
      {submitted && Object.keys(form.errors).length > 0 && <div className="alert alert-danger" role="alert"><strong>Check the highlighted fields.</strong>
        <ul className="mb-0">{Object.entries(form.errors).map(([name, error]) => <li key={name}><button type="button" className="btn btn-link p-0 text-left" onClick={() => focusError(name)}>{error}</button></li>)}</ul></div>}
      {message && <div className="alert alert-danger" role="alert">{message}</div>}
      {locked && <div className="alert alert-info" role="status">{status === 'pending' ? 'Waiting for the saved club confirmation…' : 'The creation result is not confirmed.'}
        {status === 'unknown' && <><button type="button" className="btn btn-link" onClick={() => track(pending.current, 'check')}>Check creation status</button>
          <button type="button" className="btn btn-link" onClick={() => track(pending.current, 'retry')}>Resume creation</button>
          <button type="button" className="btn btn-link" onClick={() => setStatus('resuming')}>Re-enter original details</button></>}
      </div>}
      {status === 'resuming' && <p className="alert alert-info">Re-enter the original details to resume the same request. A saved club will be recovered without creating another.</p>}
      <fieldset disabled={locked}>
      {section('Club & location', <>{grid(<>
        {field('clubName', 'Club name / brand name', { required: true, maxLength: 200 })}
        {field('businessName', 'Business name', { required: true, maxLength: 200 })}
        {field('country', 'Country', { required: true, choices: (options.eligibleCountries || []).map(country => ({ value: country.name, label: country.name })),
          onChange: event => form.setValues(values => ({ ...values, country: event.target.value, location: null })) })}
        <ClubAddressPicker country={options.eligibleCountries?.find(country => country.name === form.values.country)}
          locked={locked} value={form.values.location} onChange={value => form.setFieldValue('location', value)} onBusy={setAddressBusy}
          error={submitted ? form.errors.location : ''} />
      </>)}<fieldset id="club-sports" tabIndex={-1} className="mt-3"><legend className="font-size-14">Sports *</legend>
        <div className="club-choice-list">{(options.activeSports || []).map(sport => <label key={sport}><input type="checkbox" checked={form.values.sports.includes(sport)}
          onChange={event => { form.setFieldTouched('sports', true, false); form.setFieldValue('sports', event.target.checked ? [...form.values.sports, sport] : form.values.sports.filter(value => value !== sport)) }} />
          {sport.charAt(0).toUpperCase() + sport.slice(1)}</label>)}</div>
        {submitted && <small className="text-danger">{form.errors.sports}</small>}
      </fieldset><label className="mt-3"><input type="checkbox" checked={form.values.partnerClub} onChange={event => form.setFieldValue('partnerClub', event.target.checked)} /> Partner club</label></>)}
      {section('Owner account & invitation', <>{grid(<>
        {field('ownerFirstName', 'Owner first name', { required: true, maxLength: 100 })}
        {field('ownerLastName', 'Owner last name', { required: true, maxLength: 100 })}
        {field('ownerEmail', 'Owner email', { required: true, type: 'email', maxLength: 254 })}
        {field('ownerPhone', 'Owner phone (optional)', { type: 'tel', maxLength: 30 })}
      </>)}<p className="text-muted mt-3 mb-0">The owner stays Pending until they set a password. The club becomes Activated after creation is confirmed. SportyaOS sign-in requires Organizer and SportyaOS access to be enabled in the optional Organizer section below.</p></>)}
      {section('Public contact', <>{grid(<>
        {field('contactFirstName', 'Contact first name (optional)', { maxLength: 100 })}
        {field('contactLastName', 'Contact last name (optional)', { maxLength: 100 })}
        {field('contactEmail', 'Public contact email (optional)', { type: 'email', maxLength: 254 })}
        {field('contactPhone', 'Public contact phone (optional)', { type: 'tel', maxLength: 30 })}
        {field('website', 'Website (optional)', { type: 'url', maxLength: 1000 })}
      </>)}</>)}
      {section('Facilities & opening hours', <details><summary>Facilities <span className="text-muted">Optional</span></summary>
        <p className="text-muted mt-3">Leave unconfirmed details empty. Not provided is different from closed or zero courts. Opening times use the club’s local time.</p>
        {form.values.sports.map(sport => <fieldset key={sport} className="mb-3"><legend className="font-size-14">{sport.charAt(0).toUpperCase() + sport.slice(1)}</legend>
          {field(sport + 'Courts', sport.charAt(0).toUpperCase() + sport.slice(1) + ' courts (optional)', { type: 'number', min: 0, max: 1000, step: 1, placeholder: 'Not provided' })}
          <div className="club-choice-list mt-2">{(options.courtSurfaces || []).filter(surface => surface.sports?.includes(sport)).map(surface => <label key={surface.key}>
            <input type="checkbox" checked={form.values[sport + 'Surfaces'].includes(surface.key)} onChange={event => form.setFieldValue(sport + 'Surfaces', event.target.checked
              ? [...form.values[sport + 'Surfaces'], surface.key] : form.values[sport + 'Surfaces'].filter(value => value !== surface.key))} />{surface.name}
          </label>)}</div>
        </fieldset>)}
        <h6 className="mt-3">Opening hours</h6>
        {Object.entries(clubDays).map(([day, label]) => <div key={day} className="club-hours-row mb-3">
          {field(day + 'State', label + ' schedule', { choices: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }], emptyLabel: 'Not provided' })}
          {form.values[day + 'State'] === 'open' && <>
            {field(day + 'Start', label + ' opens', clubHourField)}{field(day + 'End', label + ' closes', clubHourField)}
          </>}
        </div>)}
        <h6 className="mt-3">Amenities</h6><div className="club-choice-list">{clubAmenities.map(amenity => <label key={amenity.id}>
          <input type="checkbox" checked={form.values.amenities.includes(amenity.id)} onChange={event => form.setFieldValue('amenities', event.target.checked
            ? [...form.values.amenities, amenity.id] : form.values.amenities.filter(id => id !== amenity.id))} />{amenity.label}
        </label>)}</div>
      </details>)}
      {section('About & directions', <ClubTextBlocks form={form} locked={locked} />)}
      {section('Presentation', <details><summary>Presentation <span className="text-muted">Optional</span></summary>
        <div className="mt-3">{grid(<>{field('facebookUrl', 'Facebook URL (optional)', { type: 'url', maxLength: 1000 })}
          {field('instagramUrl', 'Instagram URL (optional)', { type: 'url', maxLength: 1000 })}{field('twitterUrl', 'X / Twitter URL (optional)', { type: 'url', maxLength: 1000 })}</>)}</div>
      </details>)}
      {section('Organizer', <details><summary>Organizer <span className="text-muted">Optional</span></summary>
        <label className="mt-3"><input type="checkbox" checked={form.values.isOrganizer} onChange={event => {
          form.setFieldValue('isOrganizer', event.target.checked); setOrganizerBusy({})
        }} /> Enable organizer details</label>
        {form.values.isOrganizer && <><p>The owner will be assigned to this new club. Additional clubs can be assigned later from the organizer profile.</p>
          <button type="button" className="btn btn-light mb-3" onClick={() => form.setValues(values => ({ ...values,
            organizerCompany: values.businessName, organizerFirstName: values.ownerFirstName, organizerLastName: values.ownerLastName,
            organizerEmail: values.ownerEmail, organizerPhone: values.ownerPhone, organizerCountry: values.country, organizerLocation: values.location,
          }))}>Use club and owner details</button>
          {grid(<>
            {field('organizerCompany', 'Organizer company name', { required: true, maxLength: 200 })}
            {field('organizerRegistrationNumber', 'Registration number', { required: true, maxLength: 100 })}
            {field('organizerFirstName', 'Organizer first name', { required: true, maxLength: 100 })}
            {field('organizerLastName', 'Organizer last name', { required: true, maxLength: 100 })}
            {field('organizerEmail', 'Organizer email', { required: true, type: 'email', maxLength: 254 })}
            {field('organizerPhone', 'Organizer phone (optional)', { type: 'tel', maxLength: 30 })}
            {field('organizerCountry', 'Organizer country', { required: true, choices: (options.eligibleCountries || []).map(country => ({ value: country.name, label: country.name })),
              onChange: event => form.setValues(values => ({ ...values, organizerCountry: event.target.value, organizerLocation: null })) })}
            <PlayerCityPicker name="organizerLocation" label="Organizer city" country={options.eligibleCountries?.find(country => country.name === form.values.organizerCountry)}
              value={form.values.organizerLocation} locked={locked} onChange={value => form.setFieldValue('organizerLocation', value)} onBusy={setOrganizerBusy}
              error={submitted ? form.errors.organizerLocation : ''} />
            {field('organizerOSAccess', 'Organizer SportyaOS access', { required: true, choices: [{ value: 'false', label: 'No organizer access' }, { value: 'true', label: 'Grant organizer access after activation' }] })}
          </>)}</>}
      </details>)}
      </fieldset>
      <EditorActions dirty={form.dirty} pending={locked} saveLabel="Create club & send invitation" message="An invitation is sent after the club and owner are saved and linked.">
        <button type="button" className="btn btn-light" onClick={() => history.push({ pathname: '/accounts/clubs', search: location.state?.clubListSearch || '',
          state: { clubListContext: location.state?.clubListContext } })}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={locked || loading || choiceBusy || Boolean(failure) || !usableSettings(options)}>{status === 'pending' ? 'Creating club…' : 'Create club & send invitation'}</button>
      </EditorActions>
    </form>}</div>
  </Container></div>
}
